// deps
import React, { ReactElement, memo, useMemo } from "react";

// internal-deps
import { format, ScreenReaderOnly } from "@swiggy-private/core-ui";
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// interfaces
import { FoodItemInfoProps } from "./FoodItemInfo";

// helpers
import { getAddonVariantString } from "@cart-ui/helpers/Item";
import { isPopFlow } from "@swiggy-private/checkout-core";

// constants
import { TEXTS } from "@cart-ui/constants";
import { DownArrow } from "@cart-ui/widgets/down-arrow";

// components
import {
    AriaHiddenCustomiseStyled,
    StyledItemName,
    ContainerStyled,
    CustomiseButtonStyled,
    CustomiseTextStyled,
    FoodSymbolStyled,
    NameStyled,
} from "./FoodItemInfoV2.styled";

const downArrowColor = "rgba(2, 6, 12, 0.45)";
const freebieText = "1 Free Delight for you";

const ACCESSIBLITY_TEXTS = {
    FOOD_QUANTITY: "Quantity {0}",
    FINAL_PRICE: "Item Total {0} Rupees",
};

function FoodItemInfoV2(props: FoodItemInfoProps): ReactElement {
    const isPopCart = isPopFlow();
    const itemCustomiseDetail = props.isCustomisable ? getAddonVariantString(props.addons, props.variants) : "";

    const accessibilityLabel = useMemo(() => {
        return ` ${props.itemTypeText} ${props.name} ${itemCustomiseDetail}
        ${format(ACCESSIBLITY_TEXTS.FOOD_QUANTITY, String(props.quantity))}
        ${format(ACCESSIBLITY_TEXTS.FINAL_PRICE, String(props.finalPrice))}`;
    }, [props.name, props.itemTypeText, props.finalPrice, itemCustomiseDetail, props.quantity]);

    return (
        <ContainerStyled className={props.className}>
            <ScreenReaderOnly as="div" text={accessibilityLabel} />
            <NameStyled data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_NAME}>
                {!props.isMerchItem && <FoodSymbolStyled vegType={props.vegType} />}
                <StyledItemName
                    variant="V2.body2.neutral.medium"
                    color="text_High_Emphasis"
                    isMerchItem={props.isMerchItem}
                >
                    {props.name}
                </StyledItemName>
            </NameStyled>
            {props.subText && (
                <Text variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                    {props.subText}
                </Text>
            )}
            {props.isFreebie && !isPopCart && (
                <Text variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                    {freebieText}
                </Text>
            )}
            {props.isCustomisable && (
                <AriaHiddenCustomiseStyled as="div" data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_VARIANTS_INFO}>
                    <CustomiseTextStyled
                        variant="V2.body3.neutral.regular"
                        color="text_Low_Emphasis"
                        onClick={props.onCustomise}
                    >
                        {itemCustomiseDetail.length > 0 ? itemCustomiseDetail : TEXTS.CUSTOMIZE}
                    </CustomiseTextStyled>
                    <CustomiseButtonStyled
                        onClick={props.onCustomise}
                        data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_CUSTOMISE_BUTTON}
                    >
                        {!props.isGroupOrderFlow && <DownArrow color={downArrowColor} />}
                    </CustomiseButtonStyled>
                </AriaHiddenCustomiseStyled>
            )}
        </ContainerStyled>
    );
}

export default memo(FoodItemInfoV2);
